import {
  HOLE_CARD_FAN_PEEK_PAIR,
  HOLE_CARD_FAN_ROTATION_STEP_DEG,
  HOLE_CARD_FAN_TIMER_CLEARANCE_PX,
  HOLE_CARD_SCALE_FACE_DOWN_LANDSCAPE,
  HOLE_CARD_SCALE_FACE_DOWN_PORTRAIT,
  SEAT_CARD_BAND_LIFT_LANDSCAPE,
  SEAT_CARD_BAND_LIFT_PORTRAIT,
  SMALL_CARD_HEIGHT,
  SMALL_CARD_WIDTH
} from 'constants/gameConstants';

/**
 * Where a FACE-DOWN hole card sits, relative to the seat's avatar centre.
 *
 * This lives apart from SeatCards because two things need it and they must not
 * disagree: the renderer, and `modes/seatEdgeInsets`, which turns the hand's
 * reach into the seat's viewport-edge clamp. They used to each restate the
 * geometry from constants, and drifted.
 *
 * Both hand shapes fan LEFT and stop HOLE_CARD_FAN_TIMER_CLEARANCE_PX short of
 * the avatar's centre, so the turn-timer countdown stays readable:
 *
 *   - HOLD'EM (2 cards) lies FLAT — a small overlapping pair.
 *   - OMAHA (4 cards) is SPLAYED around a hinge on the bottom edge of the
 *     rightmost card, so it opens up and to the left like a held hand.
 *
 * Omaha is splayed because flat it is a wide rectangle whose bottom-left corner
 * lands on the dealer marker of any right-column seat — the marker hangs off the
 * seat card's centre-facing edge, which is the same side the cards fan to
 * (reported 2026-08-25 on Frank, Greta, Hugo and Ivy). A fan is widest at the
 * TOP, where nothing else lives, and narrows to its hinge at the bottom, where
 * the marker is. It also reaches slightly LESS far left than the flat spread it
 * replaces, so the seat's edge clamp gets a little looser rather than tighter.
 */

export type HoleCardOffset = { x: number; y: number; rotation: number };

export const faceDownCardScale = (isPortrait: boolean): number =>
  isPortrait ? HOLE_CARD_SCALE_FACE_DOWN_PORTRAIT : HOLE_CARD_SCALE_FACE_DOWN_LANDSCAPE;

/** Omaha and bigger. A pair never reached the dealer marker, so it stays flat. */
export const isFannedHand = (cardCount: number): boolean => cardCount > 2;

/** Vertical anchor shared with the face-up spread, so the band cannot jump on the flip. */
const bandTop = (scaledHeight: number, isPortrait: boolean): number =>
  -(scaledHeight * (isPortrait ? SEAT_CARD_BAND_LIFT_PORTRAIT : SEAT_CARD_BAND_LIFT_LANDSCAPE));

export const faceDownCardOffset = (
  index: number,
  cardCount: number,
  isPortrait: boolean
): HoleCardOffset => {
  const scale = faceDownCardScale(isPortrait);
  const scaledWidth = SMALL_CARD_WIDTH * scale;
  const scaledHeight = SMALL_CARD_HEIGHT * scale;
  const y = bandTop(scaledHeight, isPortrait);
  // The rightmost card, unrotated: the one every other card is a rotation OF,
  // and the one that sets the clearance on the timer side.
  const baseX = -HOLE_CARD_FAN_TIMER_CLEARANCE_PX - scaledWidth;

  if (!isFannedHand(cardCount)) {
    const peek = scaledWidth * HOLE_CARD_FAN_PEEK_PAIR;
    return {
      x: baseX - (cardCount - 1 - index) * peek,
      y,
      rotation: index === 0 ? -8 : 0
    };
  }

  // Anticlockwise from the rightmost card, so the hand opens up and to the LEFT
  // over empty felt. Rotation only ever moves a card left and up from the base,
  // which is why the timer clearance survives it for free.
  // Steps away from the unrotated rightmost card. Guarded against negative zero,
  // which `-(0) * step` produces and which reads as a different value to
  // Object.is (and so to `toBe(0)`).
  const steps = cardCount - 1 - index;
  const rotationDeg = steps === 0 ? 0 : -steps * HOLE_CARD_FAN_ROTATION_STEP_DEG;
  const theta = rotationDeg * (Math.PI / 180);
  // The hinge sits ON the base card's bottom edge, and has to stay there. Lift
  // it and the corners below it swing RIGHT as they rotate, straight back over
  // the timer the clearance just bought; drop it and the fan widens for nothing.
  const hingeX = baseX + scaledWidth / 2;
  const hingeY = y + scaledHeight;
  const dx = baseX - hingeX;
  const dy = y - hingeY;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  // Cards rotate about their own top-left (SeatCards' cardStyle sets
  // transformOrigin), so place that corner where rotating the whole card about
  // the hinge would have put it.
  return {
    x: hingeX + dx * cos - dy * sin,
    y: hingeY + dx * sin + dy * cos,
    rotation: rotationDeg
  };
};

/**
 * How far the hand reaches LEFT of the avatar centre, in unscaled px.
 *
 * The first card's top-left is the leftmost point in both shapes: fanned, its
 * rotation is anticlockwise, which puts its other three corners to the RIGHT of
 * that one.
 */
export const faceDownLeftReach = (cardCount: number, isPortrait: boolean): number =>
  -faceDownCardOffset(0, cardCount, isPortrait).x;
